/* eslint-disable no-unused-vars */
import {CheckBox, Layout} from '@ui-kitten/components';
import React, {useCallback, useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {px} from '../../utils';
import TitleRow from './TitleRow';
import useAZRevision from '../../hooks/useAZRevision';

const CLASS_PAIR_PATTERN = /class\s*[12]/i;

const isPairedClassType = (subjectType) =>
  CLASS_PAIR_PATTERN.test(String(subjectType || ''));

const getItemsToToggle = ({isAZRevision, list, row}) => {
  if (!isAZRevision || !isPairedClassType(row?.subjectType)) {
    return [row];
  }

  const pairedItems = list.filter(
    (item) =>
      item.name === row.name &&
      item.languageCode === row.languageCode &&
      isPairedClassType(item.subjectType),
  );

  return pairedItems.length ? pairedItems : [row];
};

const CheckboxList = ({
  title,
  list,
  selectedItems,
  setSelectedItems,
  containerStyle,
}) => {
  const isAZRevision = useAZRevision();

  const [allChecked, setAllChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [isListVisible, setIsListVisible] = useState(true);

  const updateAllCheckedStatus = useCallback(() => {
    const someChecked =
      list?.length &&
      list?.some((listItem) =>
        selectedItems.some(
          (selectedItem) => listItem.value === selectedItem.value,
        ),
      );
    const everyChecked =
      list?.length &&
      list?.every((listItem) =>
        selectedItems.some(
          (selectedItem) => listItem.value === selectedItem.value,
        ),
      );

    if (someChecked && !everyChecked) {
      setAllChecked(true);
      setIndeterminate(true);
    } else if (!someChecked && !everyChecked) {
      setAllChecked(false);
      setIndeterminate(false);
    } else if (everyChecked) {
      setAllChecked(true);
      setIndeterminate(false);
    }
  }, [list, selectedItems]);

  useEffect(() => {
    updateAllCheckedStatus();
  }, [updateAllCheckedStatus]);

  const renderRow = (row) => {
    const checked = selectedItems.some(
      (selectedItem) => selectedItem.value === row.value,
    );

    const onChange = (nextChecked) => {
      const itemsToToggle = getItemsToToggle({isAZRevision, list, row});
      const valuesToToggle = new Set(itemsToToggle.map((item) => item.value));

      if (nextChecked) {
        const selectedValues = new Set(
          selectedItems.map((selectedItem) => selectedItem.value),
        );
        const itemsToAdd = itemsToToggle.filter(
          (item) => !selectedValues.has(item.value),
        );
        setSelectedItems([...selectedItems, ...itemsToAdd]);
      } else {
        setSelectedItems(
          selectedItems.filter(
            (selectedItem) => !valuesToToggle.has(selectedItem.value),
          ),
        );
      }
    };

    return (
      <CheckBox
        key={row.value}
        checked={checked}
        onChange={onChange}
        style={styles.row}>
        {row.text}
      </CheckBox>
    );
  };

  return (
    <Layout style={containerStyle}>
      <TitleRow
        title={title}
        type="secondary"
        iconName={
          isListVisible
            ? 'arrow-ios-upward-outline'
            : 'arrow-ios-downward-outline'
        }
        onIconClick={() => setIsListVisible(!isListVisible)}
        containerStyle={styles.title}
      />
      {list?.length && isListVisible && (
        <Layout level="1">
          {/* <CheckBox
            key="all"
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={onAllBoxChange}
            style={styles.row}>
            {'All'}
          </CheckBox> */}
          {list?.map(renderRow)}
        </Layout>
      )}
    </Layout>
  );
};

const styles = StyleSheet.create({
  row: {
    marginVertical: px(12),
    marginHorizontal: px(17),
  },
});

export default CheckboxList;
